import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, MessageSquare, Phone, Video, X, Mail, Send } from "lucide-react";

/**
 * Unified Mentor & Collaborators card — lists main client + assigned mentor +
 * collaborators in one place. Click any avatar to open a profile-preview drawer
 * (rendered via portal to avoid layout flicker) with Message / Audio / Video shortcuts.
 *
 * `context` controls the vocabulary shown for the mentor row and the empty
 * copy:
 *   • "team"          — "TEAM LEAD" / "CO-LEAD"        (team workspaces)
 *   • "employee_task" — "ASSIGNER"  / "CO-ASSIGNER"    (task-derived workspaces)
 *   • default         — "ASSIGNED MENTOR" / "CO-MENTOR"
 */
const LEAD_LABELS = {
  team:          { primary: "TEAM LEAD",       co: "CO-LEAD",        add: "+ ADD CO-LEAD" },
  employee_task: { primary: "TASK ASSIGNER",   co: "CO-ASSIGNER",    add: "+ ADD CO-ASSIGNER" },
  default:       { primary: "ASSIGNED MENTOR", co: "CO-MENTOR",      add: "+ ADD MENTOR" },
};

export default function ParticipantsCard({ participants, currentUserId, mentor, mentors = [], onStartCall, onMessage, canManageMentors = false, onAddCoMentor, onRemoveCoMentor, context, workspaceId, onParticipantsChanged }) {
  const [preview, setPreview] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [users, setUsers] = useState([]);
  const [addSearch, setAddSearch] = useState("");
  const [addUid, setAddUid] = useState("");
  const [addRole, setAddRole] = useState("Collaborator");
  const [busy, setBusy] = useState(false);
  const labels = LEAD_LABELS[context] || LEAD_LABELS.default;
  // Owner + mentor + admin can add / remove workspace-level collaborators.
  // We piggy-back on `canManageMentors` since it already carries that grant.
  const canManageParticipants = !!workspaceId && canManageMentors;

  // Lazy-load the user picker only when the add UI is going to render.
  useEffect(() => {
    if (!canManageParticipants || users.length > 0) return;
    api.get("/admin/users?role=&q=&page=1&page_size=500")
      .then((r) => setUsers((r.data?.items || r.data || []).filter((x) => x.role !== "client")))
      .catch(() => api.get("/employee/assignable")
        .then((r) => setUsers((r.data || []).filter((x) => x.role !== "client")))
        .catch(() => {}));
  }, [canManageParticipants, users.length]);

  const addParticipant = async () => {
    if (!addUid) { toast.error("Pick a user first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/workspace/${workspaceId}/participants`, {
        user_id: addUid, role_in_workspace: addRole,
      });
      if (r.data?.already) toast.info("Already a collaborator"); else toast.success("Collaborator added");
      setAddUid(""); setAddSearch("");
      onParticipantsChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  const removeParticipant = async (p) => {
    if (p.is_main_client || p.is_owner) { toast.error("Owner can't be removed"); return; }
    if (!window.confirm(`Remove ${p.name} from this workspace?`)) return;
    try {
      await api.delete(`/workspace/${workspaceId}/participants/${p.user_id}`);
      toast.success("Removed");
      onParticipantsChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };
  // Assemble a canonical "mentors list": primary `mentor` prop + any extras
  // passed through `mentors`. Each entry is normalised so the same click →
  // ProfileDrawer flow works for every mentor row.
  const mentorRows = [];
  if (mentor) mentorRows.push({ ...mentor, _kind: "primary" });
  for (const m of (mentors || [])) mentorRows.push({ ...m, _kind: "co" });
  return (
    <>
      {mentorRows.length > 0 && (
        <div className="space-y-1.5 mb-3" data-testid="participants-mentor-list">
          {mentorRows.map((m, idx) => {
            const clickable = !!m.user_id;
            return (
              <div
                key={m.user_id || m.mentor_id || `m-${idx}`}
                className={`w-full flex items-center gap-3 p-3 border rounded-sm ${
                  m._kind === "primary" ? "border-emerald-200 bg-emerald-50/50" : "border-emerald-100 bg-emerald-50/30"
                }`}
                data-testid={`participants-mentor-${m.user_id || m.mentor_id || idx}`}
              >
                <button
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && setPreview({
                    user_id: m.user_id,
                    name: m.name,
                    picture: m.photo_url || m.picture,
                    role_in_workspace: m._kind === "primary" ? labels.primary.toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : labels.co.toLowerCase().replace(/^\w/, (c) => c.toUpperCase()),
                    headline: m.headline,
                  })}
                  className={`flex items-center gap-3 flex-1 text-left min-w-0 ${clickable ? "hover:opacity-80" : "opacity-70 cursor-default"}`}
                >
                  {(m.photo_url || m.picture) ? (
                    <img src={m.photo_url || m.picture} alt="" className="w-10 h-10 rounded-full object-cover bg-slate-200" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center kb-mono text-xs text-emerald-700">
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-kb-ink truncate">{m.name}</div>
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-emerald-700">{m._kind === "primary" ? labels.primary : labels.co}</div>
                  </div>
                </button>
                {m._kind === "co" && canManageMentors && (
                  <button type="button" onClick={() => onRemoveCoMentor?.(m.mentor_id)} className="p-1 text-rose-500 hover:text-rose-700" title="Remove co-mentor" data-testid={`remove-co-mentor-${m.mentor_id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
                <UserCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              </div>
            );
          })}
          {canManageMentors && (
            <button
              type="button"
              onClick={() => setShowPicker(true)}
              className="w-full kb-mono text-[10px] uppercase tracking-wider text-kb-primary border border-dashed border-slate-300 rounded-sm py-2 hover:bg-slate-50"
              data-testid="add-co-mentor-btn"
            >
              + {labels.add.replace("+ ", "")}
            </button>
          )}
        </div>
      )}
      {showPicker && (
        <MentorPickerModal
          onClose={() => setShowPicker(false)}
          onPick={(mm) => { setShowPicker(false); onAddCoMentor?.(mm); }}
          existingMentorIds={mentorRows.filter((m) => m.mentor_id).map((m) => m.mentor_id)}
        />
      )}

      <div className="space-y-1.5">
        {participants.length === 0 ? (
          <div className="text-xs text-kb-ink-muted text-center py-3">No participants yet.</div>
        ) : participants.map((p) => {
          const isMe = p.user_id === currentUserId;
          const canRemove = canManageParticipants && !isMe && !p.is_main_client && !p.is_owner;
          return (
            <div key={p.user_id} className={`w-full flex items-center gap-2.5 p-2 border border-slate-200 rounded-sm bg-white transition-colors ${isMe ? "opacity-70" : "hover:border-kb-primary hover:bg-kb-primary/5"}`} data-testid={`participant-${p.user_id}`}>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (!isMe) setPreview(p); }}
                disabled={isMe}
                className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
              >
                {p.picture ? (
                  <img src={p.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-[10px] text-kb-primary">
                    {p.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-kb-ink truncate">{p.name} {isMe && <span className="kb-mono text-[9px] text-kb-ink-light">(you)</span>}</div>
                  <div className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-muted">
                    {p.role_in_workspace}{p.is_main_client ? " · MAIN CLIENT" : ""}
                  </div>
                </div>
              </button>
              {canRemove && (
                <button type="button" onClick={() => removeParticipant(p)} className="p-1 text-rose-500 hover:text-rose-700 shrink-0" title="Remove from workspace" data-testid={`participant-remove-${p.user_id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
        {canManageParticipants && (
          <div className="pt-2 mt-2 border-t border-dashed border-slate-200 space-y-1.5" data-testid="participants-add-row">
            <input
              list="pc-user-picker"
              placeholder="Add a collaborator…"
              className="kb-input text-xs h-7"
              value={addSearch}
              onChange={(e) => {
                const val = e.target.value;
                const match = users.find((u) => `${u.name || u.email} — ${u.email}` === val || u.email === val);
                setAddSearch(val); setAddUid(match?.user_id || "");
              }}
              data-testid="participants-add-input"
            />
            <datalist id="pc-user-picker">
              {users.slice(0, 200).map((u) => <option key={u.user_id} value={`${u.name || u.email} — ${u.email}`} />)}
            </datalist>
            <div className="flex items-center gap-1.5">
              <input
                value={addRole}
                onChange={(e) => setAddRole(e.target.value)}
                placeholder="Role (Collaborator, Reviewer…)"
                className="kb-input text-xs h-7 flex-1"
                data-testid="participants-add-role"
              />
              <button type="button" onClick={addParticipant} disabled={!addUid || busy} className="kb-btn-primary text-[10px] h-7 px-2 disabled:opacity-40" data-testid="participants-add-btn">
                {busy ? "…" : "Add"}
              </button>
            </div>
          </div>
        )}
      </div>

      {preview && (
        <ProfileDrawer
          partner={preview}
          onClose={() => setPreview(null)}
          onMessage={() => { setPreview(null); onMessage?.(preview); }}
          onAudio={() => { setPreview(null); onStartCall?.(true, preview); }}
          onVideo={() => { setPreview(null); onStartCall?.(false, preview); }}
        />
      )}
    </>
  );
}

function ProfileDrawer({ partner, onClose, onMessage, onAudio, onVideo }) {
  const [dmText, setDmText] = useState("");
  const [sending, setSending] = useState(false);

  // Lock body scroll while modal is open + compensate for scrollbar removal
  // to prevent the page from flickering / shifting when the drawer mounts.
  useEffect(() => {
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sendDm = async () => {
    if (!dmText.trim()) return;
    setSending(true);
    try {
      await api.post("/workspace/dm/send", { to_user_id: partner.user_id, body: dmText.trim() });
      toast.success(`Sent to ${partner.name}`);
      setDmText("");
      onClose();
    } catch (e) {
      toast.error("Send failed");
    } finally {
      setSending(false);
    }
  };

  const node = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="profile-drawer"
    >
      <div
        className="relative bg-white rounded-sm border border-slate-200 shadow-xl w-[min(420px,calc(100vw-32px))]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-kb-ink-muted hover:text-kb-ink p-2"
          data-testid="profile-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="p-5 text-center">
          {partner.picture ? (
            <img src={partner.picture} alt="" className="w-20 h-20 rounded-full object-cover mx-auto border border-slate-200" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-kb-primary/10 flex items-center justify-center kb-mono text-2xl text-kb-primary mx-auto">
              {partner.name?.[0]?.toUpperCase()}
            </div>
          )}
          <h3 className="font-serif text-xl text-kb-ink mt-3">{partner.name}</h3>
          <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
            {partner.role_in_workspace}{partner.affiliation ? ` · ${partner.affiliation}` : ""}
          </div>
          {partner.email && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-kb-ink-muted">
              <Mail className="w-3 h-3" /> {partner.email}
            </div>
          )}
          <div className="mt-5 grid grid-cols-3 gap-2">
            <button type="button" onClick={onMessage} className="kb-btn-outline text-xs justify-center" data-testid="profile-message-btn">
              <MessageSquare className="w-3.5 h-3.5" /> Message
            </button>
            <button type="button" onClick={onAudio} className="kb-btn-outline text-xs justify-center" data-testid="profile-audio-btn">
              <Phone className="w-3.5 h-3.5" /> Audio
            </button>
            <button type="button" onClick={onVideo} className="kb-btn-primary text-xs justify-center" data-testid="profile-video-btn">
              <Video className="w-3.5 h-3.5" /> Video
            </button>
          </div>
          <div className="mt-5 pt-4 border-t border-slate-200 flex gap-2">
            <input
              className="kb-input text-sm flex-1"
              placeholder={`Quick message to ${partner.name?.split(" ")[0]}…`}
              value={dmText}
              onChange={(e) => setDmText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendDm()}
              data-testid="profile-quick-dm-input"
            />
            <button type="button" onClick={sendDm} disabled={!dmText.trim() || sending} className="kb-btn-primary text-sm" data-testid="profile-quick-dm-send">
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Render via portal to escape any ancestor stacking/transform/overflow contexts
  // (this is the root cause of the click-to-open flicker).
  return createPortal(node, document.body);
}


// ---- Compact mentor picker (co-mentor add flow) ---------------------------
function MentorPickerModal({ onClose, onPick, existingMentorIds = [] }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/mentors").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);
  const filtered = items.filter((m) => {
    if (existingMentorIds.includes(m.id)) return false;
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (m.name || "").toLowerCase().includes(t) || (m.expertise || []).join(" ").toLowerCase().includes(t);
  });
  return createPortal(
    <div className="fixed inset-0 z-[170] bg-black/40 flex items-center justify-center p-4" data-testid="mentor-picker-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-md w-full border border-slate-200 max-h-[80vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-serif text-lg text-kb-ink">Add co-mentor</h3>
          <button type="button" onClick={onClose} className="p-1 text-kb-ink-muted hover:text-kb-ink"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 border-b border-slate-200">
          <input autoFocus className="kb-input text-sm w-full" placeholder="Search mentors by name or expertise…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mentor-picker-search" />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="text-xs text-kb-ink-muted text-center py-6">Loading…</div> :
            filtered.length === 0 ? <div className="text-xs text-kb-ink-muted text-center py-6">No matches.</div> :
            filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onPick(m)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                data-testid={`mentor-picker-${m.id}`}
              >
                {m.photo_url ? (
                  <img src={m.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center kb-mono text-xs text-emerald-700">{m.name?.[0]}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-kb-ink truncate">{m.name}</div>
                  <div className="kb-mono text-[10px] text-kb-ink-muted truncate">{(m.expertise || []).slice(0, 4).join(" · ")}</div>
                </div>
              </button>
            ))
          }
        </div>
      </div>
    </div>,
    document.body,
  );
}
